//
//  GoogleHealthAccountLinkage.tsx
//
//  isana.net,inc. Proprietary and Confidential
//
//  連携状態を判定し、新規 / 既存(移行) / 連携済み で表示を出し分けるオーケストレーター（E1-5）。
//  フロー固有のUI・処理は子コンポーネントに分離する（将来の改修・廃止を容易にするため）:
//   - 新規ユーザ      → <NewUserRegistration>（研究番号入力）
//   - 既存(移行)ユーザ → <ExistingUserMigration>（確認操作なしで自動移行＋スピナー）
//

import { type ReactElement, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import {
  getRegistrationStatus,
  RegistrationStatus,
} from "../network/googleHealth/api";
import { OFFICIAL_WEB_SITE_NAME, OFFICIAL_WEB_SITE_URL } from "../utils/env";
import IsnLogo from "../component/IsnLogo";
import NewUserRegistration from "./NewUserRegistration";
import ExistingUserMigration from "./ExistingUserMigration";
import UserReactivation from "./UserReactivation";
import "../styles.css";

// 連携画面の見出し・説明文は製品共通の固定文言（テナント非依存）。
// テナントごとに変わるのは研究番号ラベル・グループ選択のみ（環境変数）。
const PAGE_HEADING = "FITINSIGHT データ連携手続き";
const PAGE_DESCRIPTION =
  "ご利用にあたり、ユーザー情報のご登録をお願いいたします。";

// Migrating は既存(移行)ユーザ向け：確認操作なしで自動的に再連携する（E1-5）。
// Reactivating は解除済み(isUsed=false)ユーザの再連携：既存番号で自動再有効化する（E3-1）。
// ReactivateConfirm は再連携時に「既存番号で再連携するか／番号を変更するか」を尋ねる（E6-1）。
enum PageState {
  Loading = "loading",
  Form = "form",
  Migrating = "migrating",
  ReactivateConfirm = "reactivate-confirm",
  Reactivating = "reactivating",
  Error = "error",
  ApiError = "api-error",
}

const GoogleHealthAccountLinkage = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  // E5-11: token はクエリでなくフラグメント（#token=…）で受け取る。
  // フラグメントはサーバへ送信されないため、ログ/Referer/DASTへの露出を防ぐ。
  const token =
    new URLSearchParams(location.hash.replace(/^#/, "")).get("token") ?? "";

  const [pageState, setPageState] = useState<PageState>(PageState.Loading);
  const [healthUserId, setHealthUserId] = useState("");
  // 再連携(reactivate)用：既存台帳の研究番号・所属グループを引き継ぐ（再入力不要）。
  const [reactivateInfo, setReactivateInfo] = useState<{
    fitinsightUniqueId: string;
    belongingGroup: string;
  } | null>(null);

  useEffect(() => {
    if (!token) {
      setPageState(PageState.Error);
      return;
    }
    getRegistrationStatus(token)
      .then(({ healthUserId: verifiedId, status, userInfo }) => {
        setHealthUserId(verifiedId);
        if (status === RegistrationStatus.Registered) {
          // 既に連携済み → 完了画面へ
          navigate("/google-health-account-linkage-complete");
        } else if (status === RegistrationStatus.Reactivate) {
          // 解除済み → 確認操作なしで自動再有効化（isUsed=true復帰＋トークン有効化）。
          // reactivate は必ず userInfo(研究番号)を伴う契約。欠落時は空文字で進めると
          // 空の研究番号で postTenantUserInfo を呼んでしまうため、ApiError に倒す（安全側）。
          if (userInfo?.fitinsightUniqueId) {
            setReactivateInfo({
              fitinsightUniqueId: userInfo.fitinsightUniqueId,
              belongingGroup: userInfo.belongingGroup ?? "",
            });
            // E6-1: 既存番号で再連携するか／番号を変更するかを確認ダイアログで尋ねる。
            setPageState(PageState.ReactivateConfirm);
          } else {
            setPageState(PageState.ApiError);
          }
        } else if (status === RegistrationStatus.Existing) {
          // 既存(移行)ユーザ → 確認操作なしで自動移行（スピナー表示）
          setPageState(PageState.Migrating);
        } else if (status === RegistrationStatus.New) {
          // 新規ユーザ → 研究番号入力フォーム
          setPageState(PageState.Form);
        } else {
          // 想定外のstatus（不正/未知のレスポンス）。新規に誤誘導すると既存ユーザの
          // 二重登録を招くため、フォームは出さずエラー表示に倒す（安全側）。
          setPageState(PageState.ApiError);
        }
      })
      .catch(() => setPageState(PageState.ApiError));
  }, [token, navigate]);

  return (
    <>
      <IsnLogo />
      <br />
      <center>
        <h1 className="text-align-right1" style={{ fontSize: "19px" }}>
          {pageState === PageState.Migrating ||
          pageState === PageState.Reactivating
            ? "Google Healthとの連携"
            : PAGE_HEADING}
        </h1>
        {pageState !== PageState.Migrating &&
          pageState !== PageState.Reactivating && <p>{PAGE_DESCRIPTION}</p>}
        <p className="bottom_space_middle" />

        {pageState === PageState.Loading && <CircularProgress />}

        {pageState === PageState.Error && (
          <Typography color="error">
            URLが不正です。QRコードを読み取り直してください。
          </Typography>
        )}

        {pageState === PageState.ApiError && (
          <Typography color="error">
            通信エラーが発生しました。しばらく経ってから再度お試しください。
          </Typography>
        )}

        {pageState === PageState.Migrating && (
          <ExistingUserMigration token={token} />
        )}

        {pageState === PageState.Reactivating && reactivateInfo && (
          <UserReactivation
            healthUserId={healthUserId}
            fitinsightUniqueId={reactivateInfo.fitinsightUniqueId}
            belongingGroup={reactivateInfo.belongingGroup}
          />
        )}

        {
          // E6-1: 再連携の確認。「はい」で既存番号のまま再有効化、「変更する」で
          // 番号入力画面（新規連携画面を流用・既存番号をプリセット）へ。
        }
        {pageState === PageState.Form && (
          <NewUserRegistration
            healthUserId={healthUserId}
            initialFitinsightUniqueId={reactivateInfo?.fitinsightUniqueId}
            initialBelongingGroup={reactivateInfo?.belongingGroup}
          />
        )}
      </center>

      {pageState === PageState.ReactivateConfirm && reactivateInfo && (
        <Dialog
          open
          onClose={() => undefined}
          aria-labelledby="reactivate-title"
          aria-describedby="reactivate-desc"
        >
          <DialogTitle id="reactivate-title">確認</DialogTitle>
          <DialogContent id="reactivate-desc">
            <Typography color="primary" component="span">
              「{reactivateInfo.fitinsightUniqueId}」
            </Typography>
            として再連携しますか？
            <br />
            番号を変更する場合は「変更する」を押してください。
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPageState(PageState.Form)}>
              変更する
            </Button>
            <Button onClick={() => setPageState(PageState.Reactivating)}>
              はい
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <p className="bottom_space_large" />
      <p className="text-align-right">
        <a href={OFFICIAL_WEB_SITE_URL}>{OFFICIAL_WEB_SITE_NAME}</a>
        <br />
        <a href="/OSSLicense">OSSライセンス</a>
      </p>
      <br />
      <p className="text-align-right2">
        Powered by <a href="https://www.isana.net/">isana.net</a>
      </p>
    </>
  );
};

export default GoogleHealthAccountLinkage;
